import { getFinancialYearLabels } from "../../brsrFinancialYearLabels";

export const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFB9D1EA" },
};

export const SUBHEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

export const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

export const CALC_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

const THIN_BLACK = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

export const BODY_FONT = { name: "Calibri", size: 11 };
export const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

export const COL_D = 4;
export const COL_E = 5;
export const COL_F = 6;
export const COL_G = 7;
export const COL_H = 8;
export const COL_I = 9;
export const COL_J = 10;
export const COL_K = 11;
export const COL_L = 12;
export const COL_M = 13;
export const COL_N = 14;
export const COL_END = COL_N;
export const COL_LABEL_END = COL_H;
export const COL_VALUE_START = COL_I;
export const COL_VALUE_END = COL_N;

const LINE_HEIGHT = 15;
const MIN_ROW_HEIGHT = 18;

function estimateRowHeight(text, colWidth, minHeight = MIN_ROW_HEIGHT) {
  const str = String(text ?? "");
  if (!str.trim()) return minHeight;
  const charsPerLine = Math.max(8, Math.floor(colWidth * 0.9));
  const lineCount = str.split(/\r?\n/).reduce(
    (total, part) => total + Math.max(1, Math.ceil(part.length / charsPerLine)),
    0
  );
  return Math.max(minHeight, lineCount * LINE_HEIGHT + 8);
}

function setAutoRowHeight(ws, row, parts) {
  ws.getRow(row).height = parts.reduce(
    (max, { text, width }) => Math.max(max, estimateRowHeight(text, width)),
    MIN_ROW_HEIGHT
  );
}

function borderCell(cell) {
  cell.border = THIN_BLACK;
}

export function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || "Show Detail", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else {
    cell.value = value;
    cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
  }
  borderCell(cell);
  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
}

export function mergeRow(ws, row, startCol, endCol, value, options = {}) {
  if (startCol < endCol) ws.mergeCells(row, startCol, row, endCol);
  setCell(ws, row, startCol, value, options);
  for (let col = startCol + 1; col <= endCol; col++) {
    borderCell(ws.getCell(row, col));
  }
}

export function safeText(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
}

export function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return "-";
}

export function toYesNoNa(value) {
  const text = safeText(value, "");
  if (!text || text === "-") return "-";
  if (text.toLowerCase() === "na" || text.toLowerCase() === "n/a") return "NA";
  return toYesNo(value);
}

export function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "-";
  const str = String(value);
  return str.includes("%") ? str : `${str}%`;
}

export function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (!Number.isNaN(num)) return num.toFixed(2);
  return String(value);
}

export function addSectionHeader(ws, row, title, fill = HEADER_FILL) {
  mergeRow(ws, row, COL_D, COL_END, title, { bold: true, align: "center", fill });
  return row + 1;
}

export function addQuestionRow(ws, row, label, value, options = {}) {
  if (options.fullWidth || (value === "" && !options.valueCol)) {
    mergeRow(ws, row, COL_D, COL_END, label, { align: "left", vertical: "top", bold: options.bold });
    return row + 1;
  }
  const valueCol = options.valueCol || COL_VALUE_START;
  const valueEnd = options.valueEnd || COL_VALUE_END;
  const labelEnd = options.labelEnd || (valueCol > COL_D ? valueCol - 1 : COL_LABEL_END);
  mergeRow(ws, row, COL_D, labelEnd, label, { align: "left", vertical: "top", bold: options.bold });
  mergeRow(ws, row, valueCol, valueEnd, value ?? "", {
    align: options.valueAlign ?? (options.link ? "center" : "left"),
    vertical: options.valueVertical || "top",
    link: options.link,
  });
  setAutoRowHeight(ws, row, [
    { text: label, width: 52 },
    { text: value, width: 58 },
  ]);
  return row + 1;
}

export function addTextAnswerRow(ws, row, label, text) {
  const value = safeText(text);
  mergeRow(ws, row, COL_D, COL_LABEL_END, label, { align: "left", vertical: "top" });
  mergeRow(ws, row, COL_VALUE_START, COL_VALUE_END, value, { align: "left", vertical: "top" });
  setAutoRowHeight(ws, row, [
    { text: label, width: 52 },
    { text: value, width: 58 },
  ]);
  return row + 1;
}

export function addShortAnswerRow(ws, row, label, value, options = {}) {
  mergeRow(ws, row, COL_D, COL_LABEL_END, label, { align: "left", vertical: "top" });
  mergeRow(ws, row, COL_VALUE_START, COL_VALUE_END, value ?? "", {
    align: options.align || "center",
    vertical: "top",
    link: options.link,
  });
  setAutoRowHeight(ws, row, [{ text: label, width: 52 }]);
  return row + 1;
}

export function addShowDetailRow(ws, row, label, text) {
  const hasText = text !== null && text !== undefined && String(text).trim() !== "";
  if (hasText) return addTextAnswerRow(ws, row, label, text);
  return addShortAnswerRow(ws, row, label, "Show Detail");
}

export function addShowDetailLinkRow(ws, row, label, sheetName) {
  return addShortAnswerRow(ws, row, label, "Show Detail", {
    align: "center",
    link: `#'${sheetName}'!D3`,
  });
}

export function setPrinciple5ColumnWidths(ws) {
  const widths = {
    A: 2, B: 2, C: 2, D: 24, E: 12, F: 12, G: 12, H: 12, I: 14,
    J: 14, K: 14, L: 14, M: 14, N: 14,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
}

export function getFyLabels(reportingYear = "") {
  const fy = getFinancialYearLabels(reportingYear);
  return {
    current: fy.currentYear ? `FY (${fy.currentYear})` : "FY (Current Financial Year)",
    previous: fy.previousYear ? `PY (${fy.previousYear})` : "PY (Previous Financial Year)",
  };
}

function wageRow(ws, row, label, curr, prev) {
  setCell(ws, row, COL_D, label, { align: "left" });
  setCell(ws, row, COL_E, formatNumber(curr?.total), { align: "center" });
  setCell(ws, row, COL_F, formatNumber(curr?.equalWageMin?.number), { align: "center" });
  setCell(ws, row, COL_G, formatPercent(curr?.equalWageMin?.percentage), { align: "center" });
  setCell(ws, row, COL_H, formatNumber(curr?.equalWageMax?.number), { align: "center" });
  setCell(ws, row, COL_I, formatPercent(curr?.equalWageMax?.percentage), { align: "center" });
  setCell(ws, row, COL_J, formatNumber(prev?.total), { align: "center" });
  setCell(ws, row, COL_K, formatNumber(prev?.equalWageMin?.number), { align: "center" });
  setCell(ws, row, COL_L, formatPercent(prev?.equalWageMin?.percentage), { align: "center" });
  setCell(ws, row, COL_M, formatNumber(prev?.equalWageMax?.number), { align: "center" });
  setCell(ws, row, COL_N, formatPercent(prev?.equalWageMax?.percentage), { align: "center" });
}

export function addTrainingBlock(ws, row, block, sectionLabel, rowLabels, keys, fyCurrent, fyPrevious) {
  row = addQuestionRow(ws, row, sectionLabel, "", { fullWidth: true, bold: true });

  mergeRow(ws, row, COL_D, COL_D, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_G, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_H, COL_J, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "Total (A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_F, "No. (B)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, "% (B / A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "Total (D)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "No. (E)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "% (E / D)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  rowLabels.forEach((label, idx) => {
    const key = keys[idx];
    const curr = block?.currentFinancialYear?.[key] || {};
    const prev = block?.previousFinancialYear?.[key] || {};
    setCell(ws, row, COL_D, label, { align: "left" });
    setCell(ws, row, COL_E, formatNumber(curr?.total), { align: "center" });
    setCell(ws, row, COL_F, formatNumber(curr?.noOfEmployee), { align: "center" });
    setCell(ws, row, COL_G, formatPercent(curr?.percentage), { align: "center" });
    setCell(ws, row, COL_H, formatNumber(prev?.total), { align: "center" });
    setCell(ws, row, COL_I, formatNumber(prev?.noOfEmployee), { align: "center" });
    setCell(ws, row, COL_J, formatPercent(prev?.percentage), { align: "center" });
    row += 1;
  });

  return row;
}

export function addMinimumWagesBlock(ws, row, wages, categoryKey, sectionLabel, fyCurrent, fyPrevious) {
  row = addQuestionRow(ws, row, sectionLabel, "", { fullWidth: true, bold: true });

  mergeRow(ws, row, COL_D, COL_D, "Category", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_E, COL_I, fyCurrent, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_J, COL_N, fyPrevious, { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "Total (A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_F, COL_G, "Equal to Minimum Wage", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_H, COL_I, "More than Minimum Wage", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "Total (D)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_K, COL_L, "Equal to Minimum Wage", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  mergeRow(ws, row, COL_M, COL_N, "More than Minimum Wage", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  setCell(ws, row, COL_D, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_E, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_F, "No. (B)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_G, "% (B / A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_H, "No. (C)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_I, "% (C / A)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_J, "", { fill: TABLE_HEADER_FILL });
  setCell(ws, row, COL_K, "No. (E)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_L, "% (E / D)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_M, "No. (F)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  setCell(ws, row, COL_N, "% (F / D)", { bold: true, fill: TABLE_HEADER_FILL, align: "center" });
  row += 1;

  const curr = wages?.currentFinancialYear?.[categoryKey] || {};
  const prev = wages?.previousFinancialYear?.[categoryKey] || {};
  [["Male", "male"], ["Female", "female"], ["Total", "totalAddition"]].forEach(([label, key]) => {
    wageRow(ws, row, label, curr?.[key], prev?.[key]);
    row += 1;
  });

  return row;
}

export function normalizeOtherRows(other) {
  if (!other) return [];
  if (Array.isArray(other)) {
    return other
      .map((item) => ({
        name: item?.others || item?.name || item?.assessmentName || "",
        percentage: item?.percentageValue ?? item?.percentage ?? item?.plantsOffices ?? "",
      }))
      .filter((item) => item.name || item.percentage !== "");
  }
  if (Array.isArray(other?.list)) {
    return normalizeOtherRows(other.list);
  }
  if (other?.others || other?.percentageValue) {
    return [{ name: other.others, percentage: other.percentageValue }];
  }
  return [];
}
